import type {Message} from '../../page/bridge';
import type {Config} from '../types/types';

export type Params = Pick<Config, 'surveyId' | 'styles' | 'domain' | 'darkTheme'> & {
	iframeScrolling: 'auto' | 'yes' | 'no';
	onClose?: () => void;
};

const defaultParams: Params = {
	surveyId: '',
	domain: 'e.mail.ru',
	iframeScrolling: 'auto',
};

export class FrameLoader {
	private params: Params;

	private frame: HTMLIFrameElement | null = null;

	constructor(params: Pick<Params, 'surveyId'> & Partial<Omit<Params, 'surveyId'>>) {
		this.params = {
			...defaultParams,
			...params,
		};

		this.handlePostMessage = this.handlePostMessage.bind(this);
	}

	open() {
		const src = this.createSrc();
		const {iframeScrolling, styles} = this.params;
		const frame = document.createElement('iframe');

		frame.frameBorder = '0';
		frame.scrolling = iframeScrolling;
		frame.src = src;
		frame.width = '320px';
		frame.height = '120px';
		frame.style.border = 'none';
		frame.style.display = 'block';
		frame.style.position = 'fixed';
		frame.style.zIndex = String(styles?.zIndex || 500);
		frame.style.bottom = styles?.bottom || '8px';
		frame.style.left = styles?.left || '8px';
		frame.style.borderRadius = 'var(--vkui--size_border_radius_paper--regular, 12px)';
		frame.style.boxShadow = 'var(--vkui--elevation2, 0 4px 32px 0 rgba(0, 16, 61, 0.32))';
		frame.style.visibility = 'hidden';

		window.addEventListener('message', this.handlePostMessage);

		window.document?.body?.appendChild(frame);
		this.frame = frame;
	}

	getFrameOrigin() {
		const {domain} = this.params;

		return `https://${domain || defaultParams.domain}`;
	}

	createSrc() {
		const {surveyId, darkTheme} = this.params;
		const frameOrigin = this.getFrameOrigin();

		return `${frameOrigin}/feedback/nps?id=${surveyId}&mode=popup&type=csat&origin=${
			location.origin
		}&darkTheme=${darkTheme ? '1' : '0'}&noShadow=1`;
	}

	setDarkTheme(value: boolean) {
		this.params.darkTheme = !!value;

		this.sendMessage({
			action: 'setTheme',
			params: {
				darkTheme: !!value,
			},
		});
	}

	setSize({width, height}: {width?: number; height?: number}) {
		if (!this.frame) {
			return;
		}

		if (width) {
			this.frame.width = `${width}px`;
		}

		if (height) {
			this.frame.height = `${height}px`;
		}
	}

	sendMessage(data: Message) {
		const source = this.frame?.contentWindow;
		const frameOrigin = this.getFrameOrigin();

		if (!source) {
			return;
		}

		source.postMessage(
			{
				...data,
				type: 'feedback-evt',
			},
			frameOrigin,
		);
	}

	handlePostMessage(event: MessageEvent) {
		const {origin, source, data} = event;

		if (
			!this.frame ||
			origin !== this.getFrameOrigin() ||
			source !== this.frame.contentWindow ||
			!data ||
			data.type !== 'feedback-evt'
		) {
			return;
		}

		// eslint-disable-next-line sonarjs/no-small-switch
		switch (data.action) {
			case 'close': {
				return this.close();
			}
			case 'setSize': {
				return this.setSize(data.params);
			}
			case 'appLoaded': {
				return this.setFrameVisible();
			}
		}
	}

	close() {
		window.removeEventListener('message', this.handlePostMessage);
		this.frame?.remove();
		this.frame = null;
		this.params.onClose?.();
	}

	private setFrameVisible(): void {
		if (this.frame) {
			this.frame.style.visibility = 'visible';
		}
	}
}
